/**
 * 🎚️ **화면 보이기/숨기기** — 어디에 쓰느냐에 따라 켜고 끈다(FR-ADMIN-21).
 *
 * ## 왜 필요한가
 * 같은 서비스를 서로 다른 자리에 내놓는다:
 *  · 집 — 다 보여 준다
 *  · **학교 방과후 수업** — 굿즈·커뮤니티는 보이면 안 된다(학교는 수업 중 상품 노출에 민감하다)
 *  · **행사장 부스** — 그날 쓰는 것만. 나머지는 아이를 흩어지게 한다
 *
 * 예전엔 이걸 코드에 박아 뒀다(`수업 중엔 이 셋을 숨긴다`). 그러면 **운영자가 못 고친다** —
 * 학교마다 사정이 다르고, 행사마다 쓰는 것이 다르다.
 *
 * ## 🔴 목록이 **한 곳**에 있어야 한다
 * 예전에는 하단 메뉴 키가 `Landing.tsx` 안에 박혀 있고, 수업 중 숨길 목록이 `shared` 에
 * 따로 있었다. 둘이 맞아야 하는데 **아무도 안 지켰다** — 메뉴 키 하나를 고치면
 * 숨기기가 조용히 안 먹고, 수업 중에 굿즈가 그대로 보인다. 여기가 그 한 곳이다.
 * (`uiFeatures.usage.test.ts` 가 「목록에는 있는데 아무도 안 쓰는 자리」를 잡는다)
 *
 * ## 🔴 기본값은 **보이기**다
 * 새 기능을 목록에 넣었는데 운영자가 아직 아무것도 안 골랐다면 **보여야** 한다.
 * 반대로 두면 기능을 만들 때마다 아이 화면에서 조용히 사라진다.
 */

export type UiFeatureGroup = "nav" | "home" | "world" | "class";

export interface UiFeature {
  /** 화면 코드가 물어보는 이름. 바꾸면 저장해 둔 설정이 끊긴다 — 함부로 바꾸지 말 것. */
  key: string;
  group: UiFeatureGroup;
  /** 운영자가 읽을 이름. */
  label: string;
  /** 끄면 **무엇이 사라지는지**. 「굿즈샵」만 적으면 어디가 사라지는지 모른다. */
  desc: string;
  /**
   * 📚 수업 중에는 **기본으로 꺼진다**(운영자가 켜면 켜진다).
   * 예전에 코드에 박혀 있던 목록이 이것이다 — 이제 여기 한 줄로 산다.
   */
  offInClass?: boolean;
  /**
   * 🧸 **영유아 모드에서 보이는 것**(`kids`). 🔴 수업 모드와 **방향이 반대다.**
   *
   * 수업(`offInClass`)은 「이것만 숨겨」지만, 영유아 모드의 약속은
   * 「**전자책·마법·체험공간 셋만**」이다(조치계획서 ■1). 그래서 **허용 목록**이다 —
   * 여기 표시가 없으면 안 보인다.
   *
   * 숨기기로 만들면 나중에 메뉴를 하나 늘릴 때 표시를 빠뜨리는 순간 아이 화면에
   * 슬쩍 들어온다. 새 메뉴가 아이에게 보이려면 **일부러 적어야** 한다.
   */
  kidsOk?: boolean;
}

export const UI_FEATURES: readonly UiFeature[] = [
  // ── 하단 메뉴(랜딩 7칸) ───────────────────────────────
  { key: "nav.myroom", group: "nav", label: "마이룸", desc: "하단 메뉴에서 내 친구 만들기" },
  { key: "nav.library", group: "nav", label: "도서관", desc: "하단 메뉴에서 전자책으로", kidsOk: true },
  { key: "nav.verse", group: "nav", label: "체험존", desc: "하단 메뉴에서 체험공간으로", kidsOk: true },
  {
    key: "nav.event",
    group: "nav",
    label: "이벤트",
    desc: "하단 메뉴에서 이벤트 광장으로",
    offInClass: true,
  },
  {
    key: "nav.store",
    group: "nav",
    label: "굿즈샵",
    desc: "하단 메뉴에서 굿즈로. 끄면 굿즈 화면 자체도 막힌다",
    offInClass: true,
  },
  {
    key: "nav.community",
    group: "nav",
    label: "커뮤니티",
    desc: "하단 메뉴에서 마을 이야기로. 끄면 그 화면 자체도 막힌다",
    offInClass: true,
  },
  { key: "nav.settings", group: "nav", label: "설정", desc: "하단 메뉴의 설정" },

  // ── 화면(주소) ────────────────────────────────────────
  {
    key: "route.rally",
    group: "home",
    label: "경주 스탬프 랠리",
    desc: "밖에 나가서 하는 것. 끄면 랠리 화면이 안 열린다",
    offInClass: true,
  },
  {
    key: "route.ar",
    group: "home",
    label: "카메라 마법(AR)",
    desc: "책·카드에 카메라를 비추는 것",
    kidsOk: true,
  },

  // ── 전자책 안 ─────────────────────────────────────────
  /**
   * 🔴 **책 읽기 화면은 어른 것이 더 많았다**(2026-09-23 실측).
   *
   * 누를 수 있는 것 **23개** 중 아이가 쓰는 것은 넘기기와 들려주기 **둘**이었다.
   * 나머지는 마법 쪽 번호 11개(두 자리 숫자 · 26px) · 한국어/English · 자막 · AR로 보기다.
   * 아이가 가장 오래 머무는 화면인데 거기가 가장 빽빽했다. 셋을 슬롯으로 뺀다.
   */
  {
    key: "book.lang",
    group: "home",
    label: "책의 한국어/English 고르기",
    desc: "책 읽기 화면 아래의 말 고르는 칸. 끄면 지금 말로만 읽는다",
  },
  {
    key: "book.subtitle",
    group: "home",
    label: "책의 자막 켜기",
    desc: "책 읽기 화면 아래의 💬 자막 단추",
  },
  {
    key: "book.magicjump",
    group: "home",
    label: "마법 쪽으로 건너뛰기",
    desc: "책 읽기 화면 아래의 ✨ 쪽 번호 줄(6·7·12…). 끄면 넘기면서 만난다",
  },

  // ── 체험공간 안 ───────────────────────────────────────
  {
    key: "world.stars",
    group: "world",
    label: "별 모으기",
    desc: "체험공간의 별 미션 안내",
    kidsOk: true,
  },
  /**
   * 🔴 **첫 화면에서 숨긴 굿즈를 마을에서 걸어서 갔다**(2026-09-23 실측).
   * 영유아 모드인데 마을 아래 구역 이동 칸에 🛍️ 굿즈가 그대로 있었다. 숨기기가
   * 한 화면에서만 걸리면 다른 길이 그대로 열려 있다 — 이 저장소가 여러 번 겪은 모양이다.
   */
  {
    key: "world.zone.store",
    group: "world",
    label: "마을의 굿즈 구역",
    desc: "체험공간 아래 구역 이동 칸의 🛍️ 굿즈와 마을에 서 있는 굿즈 가는 문. 끄면 그 구역으로 못 간다",
    offInClass: true,
  },
  /**
   * 🔴 **구역 칸을 숨겨도 가게는 마을 곳곳에 서 있었다**(2026-09-26 실측).
   * 이벤트 구역에도 상품 가게가 있어서, 영유아 모드에서 이벤트로 걸어가면 스마트스토어로 가는
   * 문이 그대로 열렸다. 조치계획서 ■1 은 「아이 화면 안의 결제·바깥 이동 단추는 숨긴다」고 했다.
   * 굿즈 화면 자체(QR 직행·보호자 화면)는 그대로다 — 숨기는 것은 **아이가 걷는 마을 안의 가게**뿐.
   */
  {
    key: "world.store",
    group: "world",
    label: "마을 안 굿즈 가게",
    desc: "체험공간 곳곳에 서 있는 상품 가게. 끄면 가게가 사라진다(굿즈 화면은 보호자 화면·QR 로 그대로)",
    offInClass: true,
  },
  /**
   * 🔴 **아이가 눌러서 남의 히어로를 덮어쓸 수 있는 자리**(2026-09-23 실측).
   *
   * 🔴 처음엔 코드 주고받기 **묶음 전체**(내 코드 보기·불러오기·종이로 뽑기)를 숨겼다.
   * 그랬더니 **교실이 깨졌다** — 학교가 관리자 설정 없이 링크(`?class=1`)만 나눠 준 경우
   * 수업 중에도 kids 가 참이라, 태블릿을 돌려 쓰는 데 꼭 필요한 코드 주고받기가 통째로
   * 사라졌다(e2e 가 잡았다). 덜어내다 길을 끊은 것이다.
   *
   * 위험한 것은 **불러오기 하나**다 — 누르면 지금 이 기기의 히어로가 남의 것으로 덮인다.
   * 「내 코드 보기」와 「종이로 뽑기」는 보여도 해롭지 않으므로 그대로 둔다.
   */
  {
    key: "verse.codeload",
    group: "world",
    label: "📥 코드로 불러오기",
    desc: "입장 화면에서 남의 코드로 히어로 가져오기. 끄면 이 기기 히어로가 안 덮인다",
  },

  // ── 방과후 수업 ───────────────────────────────────────
  {
    key: "class.story",
    kidsOk: true,
    group: "class",
    label: "📖 내 그림책",
    desc: "입장 화면에서 그림책 쓰기",
  },
  {
    key: "class.gallery",
    kidsOk: true,
    group: "class",
    label: "🖼️ 우리 반 전시장",
    desc: "입장 화면에서 반 친구들 그림책 보기",
  },
  {
    key: "class.stage",
    kidsOk: true,
    group: "class",
    label: "🏫 화상 수업",
    desc: "수업 중일 때만 뜨는 화상 교실 입구",
  },
  {
    key: "class.teacher",
    kidsOk: true,
    group: "class",
    label: "🧑‍🏫 선생님 화면",
    desc: "수업 중일 때만 뜨는 선생님 화면 입구",
  },
] as const;

/** 저장되는 모양 — 운영자가 **직접 고른 것만** 담는다(안 고른 것은 기본값). */
export type UiFeatureDoc = Record<string, boolean>;

export function isUiFeatureKey(v: unknown): v is string {
  return typeof v === "string" && UI_FEATURES.some((f) => f.key === v);
}

/**
 * 밖에서 온 설정을 다듬는다(불변식 7). **모르는 이름은 버린다** —
 * 안 버리면 오타 하나가 저장돼서 「분명히 껐는데 그대로 보인다」가 된다.
 */
export function sanitizeUiFeatures(raw: unknown): UiFeatureDoc {
  const out: UiFeatureDoc = {};
  if (!raw || typeof raw !== "object") return out;
  for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
    if (isUiFeatureKey(k) && typeof v === "boolean") out[k] = v;
  }
  return out;
}

/**
 * 이 기능을 지금 보여줄까.
 *
 * 순서가 뜻이다: **운영자가 직접 고른 것이 가장 세다.** 그 다음이 수업 모드 기본값,
 * 마지막이 「보이기」. 학교마다 사정이 다르므로 운영자가 수업 중에도 켤 수 있어야 한다.
 */
export function uiVisible(
  key: string,
  doc: UiFeatureDoc | undefined,
  inClass = false,
  kids = false,
): boolean {
  const picked = doc?.[key];
  if (typeof picked === "boolean") return picked;
  const f = UI_FEATURES.find((x) => x.key === key);
  if (!f) return true; // 목록에 없는 이름 — 막을 근거가 없다(새 화면이 먼저 나갈 수 있다)
  // 🔴 영유아 모드는 **허용 목록**이다(`kidsOk`). 위 기본값 규칙(모르면 보이기)의 유일한 예외 —
  //    「셋만 보인다」가 약속이라, 새 메뉴는 일부러 적어야 아이에게 보인다.
  if (kids && !f.kidsOk) return false;
  return !(inClass && f.offInClass);
}

/**
 * 이 주소를 열어도 되나.
 *
 * 🔴 주소와 기능을 **여기서 잇는다.** 예전엔 숨길 주소 목록이 따로 있어서,
 * 메뉴는 감췄는데 주소를 직접 치면 그대로 열렸다(또는 그 반대).
 */
export const ROUTE_FEATURE: readonly { path: string; key: string }[] = [
  { path: "/goods", key: "nav.store" },
  { path: "/community", key: "nav.community" },
  { path: "/rally", key: "route.rally" },
  { path: "/ar", key: "route.ar" },
];

/**
 * 🔴 **영유아 모드는 여기에 안 온다** — 일부러 그렇다.
 *
 * 영유아 모드가 숨기는 것은 **아이 화면의 메뉴**지 주소가 아니다. 여기에 태우면
 * 종이책·굿즈에 인쇄된 **QR 로 상품 화면에 바로 오는 길이 막힌다** — 조치계획서 ■9 의
 * 「스토어 이동 500회」는 그 길로 세는 숫자이고, 보호자는 QR 을 찍어 들어온다.
 * 운영자가 `nav.store` 를 **직접 껐을 때**는 지금처럼 주소도 막힌다(그건 운영자의 뜻이다).
 */
export function uiRouteAllowed(
  path: string,
  doc: UiFeatureDoc | undefined,
  inClass = false,
): boolean {
  const hit = ROUTE_FEATURE.find((r) => path === r.path || path.startsWith(`${r.path}/`));
  return hit ? uiVisible(hit.key, doc, inClass) : true;
}

/**
 * 🗺 **이 마을 물건은 어느 슬롯이 정하나** — 없으면 `undefined`(늘 보인다).
 *
 * 🔴 판정은 여기 한 곳이다. 체험공간(Phaser)은 React 밖이라 화면마다 조건을 다시 짜기 쉽고,
 * 그러면 「구역 칸은 숨겼는데 마을 문으로는 간다」가 **아무 에러 없이** 생긴다(실제로 그랬다).
 * 관리자 배치 화면은 **모든 물건을 보여야** 하므로 이것을 쓰지 않는다 — 아이 화면만 거른다.
 */
export function worldObjectFeature(o: {
  type: string;
  to?: string;
  props?: Record<string, unknown>;
}): string | undefined {
  if (o.type === "portal" && o.to === "goods") return "world.zone.store";
  if (o.type === "store" || (o.props && o.props.goods_id != null && o.props.goods_id !== "")) return "world.store";
  return undefined;
}

/**
 * 🗣️ **친구의 말이 가리키는 곳** — 그 곳을 숨긴 화면에서는 말도 뺀다 (2026-09-30).
 *
 * 🔴 영유아 모드가 마을의 「굿즈 마켓」 문을 숨긴 뒤에도 병아리는 「삐약! 마켓은 이쪽이에요.」라고 했다
 * (운영 게시본 · 폰으로 걸어 보고 앎). 아이는 없는 문을 찾아 헤맨다. 말은 운영자 글이라 코드가 고칠 수 없지만,
 * **어떤 곳을 숨겼는지는 코드가 안다** — 물건을 거르는 `worldObjectFeature` 와 같은 슬롯으로 거른다.
 *
 * 낱말을 좁게 둔다: 「가게」는 황남빵 가게처럼 경주 이야기에도 나와서 넣지 않았다(넓게 잡으면 멀쩡한 말이 사라진다).
 * 관리자 대사 칸도 이 함수로 「영유아·수업 모드에서는 안 나와요」를 보여 준다 — 운영자가 이유를 안다.
 */
const LINE_POINTS_TO: readonly (readonly [RegExp, string])[] = [[/마켓|굿즈|스토어/, "world.zone.store"]];

export function npcLineFeature(line: string): string | undefined {
  for (const [re, key] of LINE_POINTS_TO) if (re.test(line)) return key;
  return undefined;
}

/** 운영자 화면에서 묶어 보여줄 이름. */
export const UI_FEATURE_GROUPS: readonly { id: UiFeatureGroup; label: string; hint: string }[] = [
  { id: "nav", label: "하단 메뉴", hint: "첫 화면 맨 아래 일곱 칸" },
  { id: "home", label: "화면", hint: "주소로 열리는 곳" },
  { id: "world", label: "체험공간 안", hint: "마을에서 보이는 것" },
  { id: "class", label: "방과후 수업", hint: "그림책·전시장·교실" },
];

/* ── 🏫 이 사이트를 지금 무엇으로 쓰나 ───────────────────── */

/**
 * 🔴 **모드는 「무엇을 숨길까」보다 앞선 결정이다.**
 *
 * 지금까지 수업 모드는 **선생님이 나눠 준 링크(`?class=1`)로 들어온 탭**만이었다.
 * 그것으로 충분한 경우가 많지만, 운영에서 두 가지가 안 됐다:
 *
 *  · **학교 전용으로 쓸 때** — 아이가 주소를 직접 치거나 즐겨찾기로 들어오면
 *    링크의 `?class=1` 이 없어서 굿즈가 그대로 보인다. 매번 링크를 다시 나눠 줄 수 없다.
 *  · **수업이 끝난 뒤** — 나눠 준 QR·링크가 그대로 남아 있다. 학기가 끝났는데도
 *    그 종이로 들어온 아이에게는 계속 굿즈가 안 보인다. 운영자는 이유를 못 찾는다.
 *
 * 그래서 **운영자가 정할 수 있어야 한다.** 기본은 `auto` — 지금까지와 똑같다
 * (셋팅을 초기화하지 않는다).
 */
export type SiteMode = "auto" | "class" | "home" | "kids";

/**
 * 🧸 **아무도 아무것도 안 골랐을 때 이것이다** (2026-09-25 · 조치계획서 ■1).
 *
 * 조치계획서가 약속한 것은 「**첫 화면의 기본값이 영유아 모드**」다. 이 서비스는 영유아와
 * 보호자를 위해 만들었으므로, 아무 설정도 없는 상태에서 아이에게 **더 안전한 쪽**으로
 * 떨어지는 것이 맞다 — 굿즈·바깥 이동 단추는 어른 확인 뒤(보호자 화면)로 간다.
 *
 * 🔴 **수업은 그대로다.** `inClassNow` 는 `kids` 를 `auto` 와 똑같이 다뤄서, 선생님이
 * 나눠 준 링크(`?class=1`)로 들어오면 여전히 수업 모드가 된다. 그리고 수업에서 쓰는
 * 자리들은 `kidsOk` 가 붙어 있어 영유아 모드에서도 보인다.
 *
 * 🔴 **이 값만 바꿔서는 운영이 안 바뀐다.** 운영자가 관리자에서 한 번이라도 저장했다면
 * 그 값(D1)이 이긴다 — 2026-09-25 현재 운영은 `mode:"auto"` 를 돌려주고 있다.
 * 운영에서 영유아 모드로 열려면 관리자 [🎚️ 수업 모드·화면 보이기]에서 **골라 게시**해야 한다.
 */
export const DEFAULT_SITE_MODE: SiteMode = "kids";

export const SITE_MODES: readonly {
  id: SiteMode;
  label: string;
  desc: string;
  when: string;
}[] = [
  {
    id: "auto",
    label: "자동",
    desc: "선생님이 나눠 준 링크로 들어온 아이만 수업 모드. 나머지는 일반.",
    when: "평소 (지금까지와 같아요)",
  },
  {
    id: "class",
    label: "🏫 방과후 수업 모드",
    desc: "누가 어떻게 들어와도 수업 모드. 굿즈·이벤트·커뮤니티가 기본으로 숨어요.",
    when: "이 사이트를 학교 수업 전용으로 쓸 때",
  },
  {
    id: "home",
    label: "🏠 일반 모드",
    desc: "수업 링크(?class=1)로 들어와도 일반 화면. 다 보여요.",
    when: "학기가 끝났는데 나눠 준 QR·링크가 아직 돌아다닐 때",
  },
  {
    id: "kids",
    label: "🧸 영유아 모드",
    desc: "첫 화면에 전자책·마법·체험공간 셋만. 굿즈·행사·소식은 [🔒 보호자] 안으로 들어가요(어른 확인 뒤에 열려요).",
    when: "아이가 혼자 눌러 보는 자리 — 중간평가 조치계획서 ■1",
  },
];

export function isSiteMode(v: unknown): v is SiteMode {
  return v === "auto" || v === "class" || v === "home" || v === "kids";
}

/**
 * 🧸 **지금 영유아 모드인가 — 판정은 여기 한 곳.**
 *
 * `inClassNow` 와 같은 이유로 함수로 둔다. 화면마다 `mode === "kids"` 를 다시 적으면
 * 나중에 조건이 하나 붙는 날 **한 화면만 안 바뀐다**(아무 에러 없이).
 *
 * 🔴 수업 모드와 **함께 켜질 수 있다.** 학교에서 영유아 모드를 쓰면 둘 다 참이고,
 * 수업 메뉴는 `kidsOk` 가 붙어 있어 그대로 보인다.
 */
export function kidsNow(mode: SiteMode | undefined): boolean {
  return mode === "kids";
}

/* ── 📱 이 기기에서 보호자가 고른 것 ────────────────────── */

/**
 * 🔴 **한 집에 두 살과 여섯 살이 같이 산다.**
 *
 * 운영자 설정은 **전체에 하나**다. 그런데 어린이집 태블릿은 영유아 모드가 맞고, 집에서
 * 형이 쓰는 폰은 일반이 맞다. 보호자가 자기 폰으로 굿즈를 보려고 **매번 두 자리 곱셈**을
 * 푸는 것도 물건을 사 줄 사람에게 거는 마찰이다.
 *
 * 그래서 **이 기기에서만** 바꿀 수 있게 한다(서버로 안 보낸다 — 불변식 1).
 *
 * 🔴 **바꾸는 자리는 어른 확인 뒤에만 둔다**(`/family`). 아이가 눌러서 바꿀 수 있으면
 * 영유아 모드는 장식이다 — 굿즈·바깥 링크로 못 가게 한다는 목적이 그 단추 하나로 무너진다.
 *
 * 🔴 **수업은 여기 없다.** 수업 모드는 반 코드가 있어야 뜻이 있다 — 고르게 하면 반도
 * 선생님도 없는 **빈 화면**을 켜 놓고 고장으로 읽는다. 그건 선생님 링크(`?class=1`)와
 * 학교 전용 설정이 정한다.
 */
export type DeviceMode = "kids" | "home";

export function isDeviceMode(v: unknown): v is DeviceMode {
  return v === "kids" || v === "home";
}

/** `art` — 보호자 화면에서 이 칸에 얹는 그림(shared UI_ART `act`). 그림을 못 받으면 label 의 이모지로. */
export const DEVICE_MODES: readonly { id: DeviceMode; label: string; desc: string; art: string }[] = [
  {
    id: "kids",
    art: "teddy",
    label: "🧸 아이 화면",
    desc: "전자책·마법·체험공간 셋만 보여요. 굿즈·행사·소식은 이 보호자 화면 안에 있어요.",
  },
  {
    id: "home",
    /**
     * 🔴 예전 이름은 「🏠 전체 화면」이었다 — 한국어에서 그 말은 **화면 꽉 채우기**(F11)다.
     * 보호자가 「화면을 크게 하는 건가?」로 읽는다. 실제 차이는 **메뉴가 다 보이는 것**이라
     * 아래 설명과도 어긋났다. 이름은 그 차이를 그대로 말한다.
     */
    art: "grid",
    label: "🏠 모든 메뉴",
    desc: "메뉴가 다 보여요. 보호자가 쓰거나 큰 아이가 쓰는 기기에 맞아요.",
  },
];

/** 지금 이 화면이 무엇인가. */
export interface ResolvedMode {
  inClass: boolean;
  kids: boolean;
  /** 보호자가 이 기기에서 바꿀 수 있나 — 수업 중에는 못 바꾼다(학교가 정한다). */
  canChoose: boolean;
}

/**
 * 🔴 **모드 판정은 여기 한 곳.** 입력이 셋(학교 · 이 기기 · 운영자)이라 화면마다 다시 짜면
 * **아무 에러 없이 갈린다** — 하단 메뉴는 영유아인데 굿즈 화면은 아니라거나.
 *
 * 세기 순서:
 *  1. **수업**(선생님 링크·학교 전용 설정) — 가장 세다. 아이가 학교 설정을 못 푼다
 *  2. **이 기기에서 보호자가 고른 것**
 *  3. **운영자 게시 설정**(바닥값)
 */
export function resolveMode(
  operator: SiteMode | undefined,
  fromClassLink: boolean,
  device?: DeviceMode,
): ResolvedMode {
  const inClass = inClassNow(operator, fromClassLink);
  // 수업 중에는 이 기기 선택을 보지 않는다 — 학교가 정한 화면이다
  if (inClass) return { inClass: true, kids: kidsNow(operator), canChoose: false };
  return { inClass: false, kids: device ? device === "kids" : kidsNow(operator), canChoose: true };
}

/** 운영자 설정 한 벌 — 모드 + 무엇을 숨길지. */
export interface UiSettings {
  mode: SiteMode;
  features: UiFeatureDoc;
}

/**
 * 밖에서 온 설정 한 벌을 다듬는다(불변식 7).
 *
 * 🔴 **옛 모양도 읽는다.** 모드가 생기기 전에는 기능만 든 객체(`{"nav.store": false}`)를
 * 저장했다. 그걸 못 읽으면 이미 저장해 둔 설정이 **게시하는 순간 통째로 날아간다.**
 */
export function sanitizeUiSettings(raw: unknown): UiSettings {
  // 아무것도 없다 — 아무도 아무것도 안 고른 상태다
  if (!raw || typeof raw !== "object") return { mode: DEFAULT_SITE_MODE, features: {} };
  const o = raw as Record<string, unknown>;
  /**
   * 🔴 **빈 객체(`{}`)는 옛 설정이 아니다.** 옛 모양은 기능 키가 **든** 것
   * (`{"nav.store": false}`)이다. 아무 키도 없는 것을 옛 모양으로 보면 「아무것도 안 골랐다」가
   * `auto` 로 떨어져, 기본값을 영유아 모드로 바꿔도 그 자리만 조용히 안 바뀐다.
   */
  const isNew = "features" in o || isSiteMode(o.mode) || Object.keys(o).length === 0;
  return {
    /**
     * 🔴 **옛 모양은 `auto` 로 남긴다 — 여기만 기본값이 다르다.**
     *
     * 모드가 생기기 전에 저장한 설정(`{"nav.store": false}`)에는 mode 칸이 없다. 그것을
     * 「아무도 안 골랐다」로 보고 영유아 모드로 올리면, **예전부터 쓰던 곳의 첫 화면이
     * 어느 날 갑자기 메뉴 넷으로 줄어든다** — 운영자는 고친 적이 없는데.
     * 새 모양인데 mode 만 이상한 값이면 그건 손상이므로 기본값으로 간다.
     */
    mode: isSiteMode(o.mode) ? o.mode : isNew ? DEFAULT_SITE_MODE : "auto",
    features: sanitizeUiFeatures(isNew ? o.features : o),
  };
}

/**
 * 🔴 **지금 수업 모드인가 — 판정은 여기 한 곳.**
 *
 * `fromTab` 은 이 탭이 수업 링크로 들어왔는지(`?class=1`).
 * 화면마다 이 조합을 다시 짜면 **아무 에러 없이 갈린다** — 하단 메뉴는 수업인데
 * 그림책은 아니라거나. 운영자는 「관리자에선 되는데 화면은 안 바뀐다」만 겪는다.
 */
export function inClassNow(mode: SiteMode | undefined, fromTab: boolean): boolean {
  if (mode === "class") return true;
  if (mode === "home") return false;
  return fromTab;
}
